import { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery, useMutation, useConvex } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { useAuthContext } from '../contexts/AuthContext';
import { useConvexAvailable } from '../contexts/ConvexAvailableContext';
import { useSyncStatus } from '../contexts/SyncStatusContext';
import type { BudgetVersionHistory, VersionedBudget } from '../types/versioning';
import {
  getConvexIdMap,
  setConvexIdMap,
  isConvexImportDone,
  setConvexImportDone,
  clearConvexImportDone,
  getLastSyncedUserId,
  setLastSyncedUserId,
  convexDocToBudget,
  budgetToConvexDoc,
  writeStorageData,
  getStorageDataRaw,
  getVersioningState,
  setVersioningState,
  type ConvexBudgetDoc,
} from './convexSyncUtils';
import { decideFirstSync, detectRemoteChanges, type SyncConflictType } from './syncDecision';
import { BUDGET_DATA_UPDATED_EVENT } from '../constants/events';
import { VERSION_CREATED_EVENT, MAX_VERSIONS_PER_BUDGET } from '../services/versioning';
import {
  getOfflineQueue,
  removeFromOfflineQueue,
  incrementRetry,
  addToOfflineQueue,
  clearOfflineQueue,
} from './offlineQueue';
import { SyncConflictDialog } from '../components/SyncConflictDialog';

const OFFLINE_QUEUE_RETRY_INTERVAL = 30_000; // 30 seconds

/**
 * When Convex is configured and user is authenticated, syncs localStorage with Convex:
 * - Subscribes to Convex and writes to localStorage (and dispatches event).
 * - One-time import of local budgets on first login (or a conflict dialog when
 *   both sides have data, or when a different account signs in on this browser).
 * - On local writes (BUDGET_DATA_UPDATED_EVENT with source !== 'convex'), pushes to Convex.
 * Renders nothing; only runs when Convex is available and user is authenticated.
 */
function ConvexSyncLayerInner() {
  const auth = useAuthContext();
  const { setConnected, setLastSyncDate } = useSyncStatus();
  const convex = useConvex();
  const convexBudgets = useQuery(api.budgets.list);
  const defaultBudgetIdConvex = useQuery(api.userPreferences.getDefaultBudgetId);
  const convexVersionHistories = useQuery(api.budgetVersions.listAllForUser);
  const importBudgetsMutation = useMutation(api.budgets.importBudgets);
  const updateBudgetMutation = useMutation(api.budgets.update);
  const createBudgetMutation = useMutation(api.budgets.create);
  const removeBudgetMutation = useMutation(api.budgets.remove);
  const setDefaultBudgetIdMutation = useMutation(api.userPreferences.setDefaultBudgetId);
  const createVersionMutation = useMutation(api.budgetVersions.create);
  const deduplicateBudgetsMutation = useMutation(api.budgets.deduplicateBudgets);

  const importInProgress = useRef(false);
  const processingQueue = useRef(false);
  const cancelledThisSession = useRef(false);
  const dedupDone = useRef(false);
  // Server-assigned revision we last pushed per Convex budget ID — used to detect
  // echoes (our own writes reflected back by the reactive query) vs genuine
  // remote changes from other sessions. Revs are server-side monotonic counters,
  // so this works regardless of clock skew between devices.
  const pushedRevs = useRef<Record<string, number>>({});
  // Track in-flight pushes per local budget ID to prevent concurrent creates/updates
  const pushInFlight = useRef<Set<number>>(new Set());
  // Local budget IDs whose createBudget mutation is already queued offline —
  // prevents queueing duplicate creates for the same budget
  const queuedCreates = useRef<Set<number>>(new Set());
  // Track the default budget Convex ID we last pushed — used to detect echoes
  // of our own default-preference writes so the star doesn't flicker.
  const pushedDefaultConvexId = useRef<string | null | undefined>(undefined);
  // Set to true when we accept remote data into localStorage — gates version history sync
  const hasAcceptedRemoteData = useRef(false);
  const [syncConflict, setSyncConflict] = useState<{
    type: SyncConflictType;
    localBudgetCount: number;
    cloudBudgetCount: number;
  } | null>(null);

  const isAuthenticated = auth?.isAuthenticated ?? false;

  // Who is signed in — used to detect account switches. Fetched imperatively
  // (not useQuery) so a backend that doesn't expose users.currentUserId yet
  // (deploy-order window) degrades to `null` instead of crashing the app.
  // undefined = still loading, null = identity unavailable.
  const [currentUserId, setCurrentUserId] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (!isAuthenticated) {
      setCurrentUserId(undefined);
      return;
    }
    let cancelled = false;
    convex.query(api.users.currentUserId).then(
      (id) => {
        if (!cancelled) setCurrentUserId(id);
      },
      () => {
        if (!cancelled) setCurrentUserId(null);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, convex]);

  // Shared import logic used by both initial import and conflict resolution
  const performImport = useCallback(async () => {
    importInProgress.current = true;
    try {
      const data = getStorageDataRaw();
      const docs = data.budgets.map((b) => {
        const doc = budgetToConvexDoc(b);
        return { ...doc, updatedAt: Date.now() };
      });
      const defaultIndex =
        data.defaultBudgetId != null
          ? data.budgets.findIndex((b) => b.id === data.defaultBudgetId)
          : -1;
      const defaultBudgetIndex = defaultIndex >= 0 ? defaultIndex : 0;

      // Include version histories in import
      const versioningState = getVersioningState();
      const versionHistories = Object.values(versioningState.histories).map((history) => ({
        localBudgetId: history.budgetId,
        versions: history.versions.map((v) => ({
          snapshot: v.budget,
          timestamp: v.timestamp instanceof Date ? v.timestamp.getTime() : v.timestamp,
          description: v.description,
        })),
      }));

      const result = await importBudgetsMutation({
        budgets: docs,
        defaultBudgetIndex,
        versionHistories: versionHistories.length > 0 ? versionHistories : undefined,
      });

      const map: Record<number, string> = {};
      result.ids.forEach((convexId, i) => {
        const localId = data.budgets[i]?.id ?? i + 1;
        map[localId] = convexId;
        // Imported docs start at rev 1 — record so the reactive echo is skipped
        pushedRevs.current[convexId] = 1;
      });
      setConvexIdMap(map);
      setConvexImportDone();
      if (typeof currentUserId === 'string') setLastSyncedUserId(currentUserId);
      return result;
    } finally {
      importInProgress.current = false;
    }
  }, [importBudgetsMutation, currentUserId]);

  // Process offline queue. On success, records the same bookkeeping the live
  // push handler records (revs, ID map, default echo) so replayed writes are
  // recognized as echoes instead of being re-accepted as remote changes.
  const processOfflineQueue = useCallback(async () => {
    if (processingQueue.current || !isAuthenticated) return;
    processingQueue.current = true;

    const queue = getOfflineQueue();
    if (queue.length === 0) {
      processingQueue.current = false;
      return;
    }

    for (const mutation of queue) {
      try {
        // Payloads are serialized to/from localStorage as Record<string, unknown>,
        // but were originally created with the correct shape for each mutation type.
        const p = mutation.payload as never;
        switch (mutation.type) {
          case 'createBudget': {
            // Fall back to the payload's budget id for queue entries persisted
            // before `meta` existed
            const localBudgetId =
              (mutation.meta?.localBudgetId as number | undefined) ??
              ((mutation.payload as { budget?: { id?: number } }).budget?.id as
                | number
                | undefined);
            // Skip if a later successful push already created this budget
            if (localBudgetId != null && getConvexIdMap()[localBudgetId] != null) break;
            const newId = await createBudgetMutation(p);
            pushedRevs.current[newId] = 1;
            if (localBudgetId != null) {
              setConvexIdMap({ ...getConvexIdMap(), [localBudgetId]: newId });
              queuedCreates.current.delete(localBudgetId);
            }
            break;
          }
          case 'updateBudget': {
            const newRev = await updateBudgetMutation(p);
            const targetId = (mutation.payload as { id: string }).id;
            // Old backends (deploy window) return the doc Id instead of a rev
            pushedRevs.current[targetId] = typeof newRev === 'number' ? newRev : 0;
            break;
          }
          case 'deleteBudget':
            await removeBudgetMutation(p);
            break;
          case 'createVersion':
            await createVersionMutation(p);
            break;
          case 'setDefaultBudget':
            pushedDefaultConvexId.current = (
              mutation.payload as { defaultBudgetId: string | null }
            ).defaultBudgetId;
            await setDefaultBudgetIdMutation(p);
            break;
        }
        removeFromOfflineQueue(mutation.id);
      } catch (error) {
        console.error(`Failed to process queued mutation ${mutation.id}:`, error);
        incrementRetry(mutation.id);
      }
    }

    processingQueue.current = false;
    setLastSyncDate(new Date());
  }, [
    isAuthenticated,
    createBudgetMutation,
    updateBudgetMutation,
    removeBudgetMutation,
    createVersionMutation,
    setDefaultBudgetIdMutation,
    setLastSyncDate,
  ]);

  // Auth state tracking + sign-out cleanup.
  // Persisted sync bookkeeping (ID map, import-done flag, offline queue) is
  // deliberately KEPT on sign-out so the same user can sign back in and resume.
  // An account switch is detected on the next sign-in and clears it then.
  useEffect(() => {
    setConnected(isAuthenticated);
    if (!isAuthenticated) {
      setLastSyncDate(null);
      pushedRevs.current = {};
      pushedDefaultConvexId.current = undefined;
      hasAcceptedRemoteData.current = false;
      dedupDone.current = false;
      cancelledThisSession.current = false;
      queuedCreates.current.clear();
    }
  }, [isAuthenticated, setConnected, setLastSyncDate]);

  // One-time dedup cleanup on auth (safe to call multiple times, no-op if no dupes)
  useEffect(() => {
    if (!isAuthenticated || !Array.isArray(convexBudgets) || dedupDone.current) return;
    dedupDone.current = true;
    deduplicateBudgetsMutation().catch((err) => console.error('Dedup failed:', err));
  }, [isAuthenticated, convexBudgets, deduplicateBudgetsMutation]);

  // Main pull flow: first-sync decision, echo detection, accepting remote data
  useEffect(() => {
    if (!isAuthenticated || convexBudgets === undefined || convexBudgets === null) return;
    if (syncConflict || importInProgress.current) return;
    // Never act before knowing WHO is signed in — otherwise the account-switch
    // check below can be skipped for one pass and stale bookkeeping from the
    // previous account would drive echo detection and reconciliation.
    if (currentUserId === undefined) return;
    const docs = convexBudgets as ConvexBudgetDoc[];
    const localData = getStorageDataRaw();

    // ACCOUNT SWITCH: a different user signed in on this browser. Clear all
    // persisted sync bookkeeping — it belongs to the previous account. The
    // first-sync decision below then decides what to do with the local data
    // (never a silent push into the new account).
    if (typeof currentUserId === 'string') {
      const lastUser = getLastSyncedUserId();
      if (lastUser != null && lastUser !== currentUserId) {
        clearConvexImportDone();
        setConvexIdMap({});
        clearOfflineQueue();
        pushedRevs.current = {};
        pushedDefaultConvexId.current = undefined;
        hasAcceptedRemoteData.current = false;
        queuedCreates.current.clear();
      } else if (lastUser == null && isConvexImportDone()) {
        // Migration: browsers synced before user tracking existed
        setLastSyncedUserId(currentUserId);
      }
    }

    const importDone = isConvexImportDone();

    const lastSyncedUser = getLastSyncedUserId();
    const decision = decideFirstSync({
      importDone,
      cancelled: cancelledThisSession.current,
      localCount: localData.budgets.length,
      cloudCount: docs.length,
      sameUserOrFresh: lastSyncedUser == null || lastSyncedUser === currentUserId,
    });

    switch (decision.action) {
      case 'wait':
        return;
      case 'import-local':
        performImport().catch((err) => console.error('Convex import failed:', err));
        return;
      case 'mark-done':
        setConvexImportDone();
        if (typeof currentUserId === 'string') setLastSyncedUserId(currentUserId);
        return;
      case 'conflict':
        setSyncConflict({
          type: decision.type,
          localBudgetCount: localData.budgets.length,
          cloudBudgetCount: docs.length,
        });
        return;
      case 'accept-remote':
        setConvexImportDone();
        if (typeof currentUserId === 'string') setLastSyncedUserId(currentUserId);
        break; // continue into the normal flow, which accepts the cloud data
      case 'proceed':
        break;
    }

    // Read ID map early — needed for remote deletion detection
    const existingIdMap = getConvexIdMap();

    // Echo detection: genuine remote changes vs echoes of our own writes.
    // Known Convex IDs missing from the query result are remote deletions.
    const { hasRemoteDeletions, hasRemoteChanges } = detectRemoteChanges({
      docs,
      pushedRevs: pushedRevs.current,
      knownConvexIds: new Set([
        ...Object.keys(pushedRevs.current),
        ...Object.values(existingIdMap),
      ]),
    });

    // If Convex is empty but we've synced before, don't overwrite localStorage
    // UNLESS the emptiness is due to remote deletions (another session deleted all budgets)
    if (docs.length === 0 && localData.budgets.length > 0 && !hasRemoteDeletions) {
      return;
    }

    // Build stable ID mapping: reuse existing local IDs for known Convex docs.
    // nextId must clear BOTH mapped IDs and unmapped local budgets' IDs, so a
    // new remote doc never gets assigned an ID that a local-only budget holds.
    const reverseMap: Record<string, number> = {};
    Object.entries(existingIdMap).forEach(([lid, cid]) => {
      reverseMap[cid] = Number(lid);
    });
    let nextId =
      Math.max(
        0,
        ...Object.keys(existingIdMap).map(Number),
        ...localData.budgets.map((b) => b.id ?? 0)
      ) + 1;

    const newIdMap: Record<number, string> = {};
    docs.forEach((doc) => {
      let localId = reverseMap[doc._id];
      if (localId == null) {
        localId = nextId++;
      }
      newIdMap[localId] = doc._id;
    });

    // Always update the ID map (new creates need their Convex IDs mapped)
    setConvexIdMap(newIdMap);

    // Returning user seeding: if we've synced before (import done) and have a
    // populated ID map but pushedRevs is empty (e.g. page refresh), seed
    // pushedRevs from the current Convex data so the initial reactive
    // query update is recognized as an echo and skipped.
    // IMPORTANT: Only skip if localStorage actually has budgets — after a
    // "Clear Local" action the import-done flag and ID map may be stale.
    // Also skip seeding when remote deletions are detected — we need to accept
    // the updated (smaller) set of docs into localStorage.
    if (
      isConvexImportDone() &&
      Object.keys(existingIdMap).length > 0 &&
      Object.keys(pushedRevs.current).length === 0 &&
      localData.budgets.length > 0 &&
      !hasRemoteDeletions
    ) {
      docs.forEach((doc) => {
        pushedRevs.current[doc._id] = doc.rev ?? 0;
      });
      return; // Skip — we already have this data locally
    }

    // Check if the remote default budget preference differs from the local one.
    // This handles the case where only the "star" (default) changed on another
    // session — the budget documents themselves are identical, so hasRemoteChanges
    // is false, but we still need to update the local default.
    // Use echo detection: if we just pushed this default, skip it.
    const resolveConvexDefaultToLocalId = () =>
      defaultBudgetIdConvex != null && typeof defaultBudgetIdConvex === 'string'
        ? (() => {
            const entry = Object.entries(newIdMap).find(
              ([, cid]) => cid === defaultBudgetIdConvex
            );
            return entry ? Number(entry[0]) : null;
          })()
        : null;
    const remoteDefaultLocalId = resolveConvexDefaultToLocalId();
    // Echo detection for default preference:
    // If we recently pushed a default preference, ignore ALL remote default
    // values until our push is echoed back. This prevents a stale remote value
    // (which hasn't caught up to our push yet) from overwriting the local default.
    const hasPendingDefaultPush = pushedDefaultConvexId.current !== undefined;
    const isDefaultEchoConfirmed =
      hasPendingDefaultPush &&
      (defaultBudgetIdConvex ?? null) === (pushedDefaultConvexId.current ?? null);
    // Clear the pending push once the echo is confirmed
    if (isDefaultEchoConfirmed) {
      pushedDefaultConvexId.current = undefined;
    }
    const hasDefaultBudgetChanged =
      !hasPendingDefaultPush &&
      remoteDefaultLocalId != null &&
      remoteDefaultLocalId !== localData.defaultBudgetId;

    if (!hasRemoteChanges && !hasDefaultBudgetChanged) {
      return;
    }

    // If only the default budget changed (no budget data changes), just update
    // the default in localStorage without rewriting all budgets.
    if (!hasRemoteChanges && hasDefaultBudgetChanged) {
      writeStorageData(
        { budgets: localData.budgets, defaultBudgetId: remoteDefaultLocalId },
        false
      );
      return;
    }

    // Remote changes detected — accept Convex data into localStorage.
    // Local-only budgets (no Convex mapping yet, e.g. created while offline
    // with their create mutation still queued) are PRESERVED, not dropped.
    const mappedLocalIds = new Set(Object.keys(newIdMap).map(Number));
    const unmappedLocalBudgets = localData.budgets.filter(
      (b) => b.id != null && !mappedLocalIds.has(b.id)
    );
    const budgets = [
      ...docs.map((doc) => {
        const localId = Object.entries(newIdMap).find(([, cid]) => cid === doc._id)?.[0];
        return convexDocToBudget(doc, localId != null ? Number(localId) : 0);
      }),
      ...unmappedLocalBudgets,
    ];

    let defaultBudgetIdLocal = remoteDefaultLocalId;

    // Fallback: if the Convex default couldn't be resolved (e.g. the preference
    // points to a deleted budget or hasn't been updated yet), pick the most
    // recent remaining budget — mirroring what deleteBudget() does locally.
    if (defaultBudgetIdLocal == null && budgets.length > 0) {
      const mostRecent = budgets.reduce((latest, current) => {
        const latestDate = new Date(latest.date).getTime();
        const currentDate = new Date(current.date).getTime();
        return currentDate > latestDate ? current : latest;
      });
      defaultBudgetIdLocal = mostRecent.id ?? null;
    }

    // Update pushed revs to reflect what we're accepting from remote
    // so subsequent echoes of these same values are also skipped
    docs.forEach((doc) => {
      pushedRevs.current[doc._id] = doc.rev ?? 0;
    });

    // Clean up pushed revs for docs that no longer exist (remote deletions)
    const currentDocIds = new Set(docs.map((d) => d._id));
    for (const id of Object.keys(pushedRevs.current)) {
      if (!currentDocIds.has(id)) {
        delete pushedRevs.current[id];
      }
    }

    hasAcceptedRemoteData.current = true;
    setLastSyncDate(new Date());

    // Update local version history BEFORE writing to storage.
    // writeStorageData dispatches an event that triggers React Query to re-read
    // via getBudget(), which checks version history first. The version history
    // must already contain the fresh data at that point.
    const vState = getVersioningState();
    let versionIdCounter = Date.now();
    for (const budget of budgets) {
      if (budget.id == null) continue;
      // Local-only budgets keep their existing history untouched
      if (!mappedLocalIds.has(budget.id)) continue;
      const history = vState.histories[budget.id];
      const newVersionId = versionIdCounter++;
      if (history) {
        // Only append a version if the budget content actually changed
        const latestVersion = history.versions[history.versions.length - 1];
        const latestSnapshot = latestVersion ? JSON.stringify(latestVersion.budget) : null;
        const currentSnapshot = JSON.stringify(budget);
        if (latestSnapshot !== currentSnapshot) {
          history.versions.push({
            id: newVersionId,
            budget: JSON.parse(currentSnapshot),
            timestamp: new Date(),
            description: 'Synced from cloud',
          });
          // Enforce version cap to prevent unbounded growth
          if (history.versions.length > MAX_VERSIONS_PER_BUDGET) {
            const removeCount = history.versions.length - MAX_VERSIONS_PER_BUDGET;
            history.versions.splice(0, removeCount);
          }
          history.currentVersionIndex = history.versions.length - 1;
        }
      } else {
        vState.histories[budget.id] = {
          budgetId: budget.id,
          versions: [{
            id: newVersionId,
            budget: JSON.parse(JSON.stringify(budget)),
            timestamp: new Date(),
            description: 'Synced from cloud',
          }],
          currentVersionIndex: 0,
        };
      }
    }
    // Remove version histories for budgets that no longer exist (remote deletions)
    const currentLocalIds = new Set(budgets.map((b) => b.id));
    for (const budgetIdStr of Object.keys(vState.histories)) {
      if (!currentLocalIds.has(Number(budgetIdStr))) {
        delete vState.histories[Number(budgetIdStr)];
      }
    }
    setVersioningState(vState, true);

    writeStorageData({ budgets, defaultBudgetId: defaultBudgetIdLocal }, false);
  }, [
    isAuthenticated,
    convexBudgets,
    currentUserId,
    defaultBudgetIdConvex,
    setLastSyncDate,
    syncConflict,
    performImport,
  ]);

  // Reconcile local-only budgets: any local budget without a Convex mapping
  // (created while signed out or while offline) is pushed to Convex once the
  // sync relationship is established.
  useEffect(() => {
    if (!isAuthenticated || !Array.isArray(convexBudgets)) return;
    // Wait for identity so the pull effect's account-switch cleanup always
    // runs first — never reconcile against another account's bookkeeping
    if (currentUserId === undefined) return;
    if (!isConvexImportDone() || importInProgress.current || syncConflict) return;

    const idMap = getConvexIdMap();
    const localData = getStorageDataRaw();
    const unmapped = localData.budgets.filter((b) => b.id != null && idMap[b.id!] == null);
    // A create already queued offline (persisted across reloads) must not be
    // duplicated by a direct push
    const pendingCreateIds = new Set(
      getOfflineQueue()
        .filter((m) => m.type === 'createBudget')
        .map(
          (m) =>
            (m.meta?.localBudgetId as number | undefined) ??
            ((m.payload as { budget?: { id?: number } }).budget?.id as number | undefined)
        )
    );

    for (const budget of unmapped) {
      const localId = budget.id as number;
      if (pushInFlight.current.has(localId) || queuedCreates.current.has(localId)) continue;
      if (pendingCreateIds.has(localId)) continue;
      pushInFlight.current.add(localId);
      (async () => {
        try {
          const payload = { ...budgetToConvexDoc(budget), updatedAt: Date.now() };
          try {
            const newId = await createBudgetMutation({ budget: payload });
            pushedRevs.current[newId] = 1;
            setConvexIdMap({ ...getConvexIdMap(), [localId]: newId });
            setLastSyncDate(new Date());
          } catch (err) {
            console.error('Failed to push local-only budget:', err);
            queuedCreates.current.add(localId);
            addToOfflineQueue({
              type: 'createBudget',
              payload: { budget: payload },
              meta: { localBudgetId: localId },
            });
          }
        } finally {
          pushInFlight.current.delete(localId);
        }
      })();
    }
  }, [
    isAuthenticated,
    convexBudgets,
    currentUserId,
    syncConflict,
    createBudgetMutation,
    setLastSyncDate,
  ]);

  // Sync version histories from Convex to localStorage — only once when remote changes were detected
  useEffect(() => {
    if (!isAuthenticated || !convexVersionHistories) return;

    // Only sync version histories when we have just accepted remote budget data.
    // This prevents overwriting local version state with stale Convex echoes.
    // Reset the flag immediately so subsequent reactive query updates don't
    // re-run this effect and overwrite locally-created versions.
    if (!hasAcceptedRemoteData.current) return;
    hasAcceptedRemoteData.current = false;

    const idMap = getConvexIdMap();
    const reverseIdMap: Record<string, number> = {};
    Object.entries(idMap).forEach(([localId, convexId]) => {
      reverseIdMap[convexId] = parseInt(localId, 10);
    });

    const histories: Record<number, BudgetVersionHistory> = {};

    convexVersionHistories.forEach((historyData) => {
      const localBudgetId = reverseIdMap[historyData.budgetId as string];
      if (localBudgetId == null) return;

      const versions = historyData.versions as Array<{
        _id?: string;
        snapshot: unknown;
        timestamp: number;
        description?: string;
      }>;

      histories[localBudgetId] = {
        budgetId: localBudgetId,
        versions: versions.map((v) => {
          // Patch the snapshot's id to match the current local ID.
          // The snapshot may contain an old local ID from a previous session.
          const snapshot = v.snapshot as Record<string, unknown>;
          return {
            id: typeof v._id === 'string' ? Number(v._id) || v.timestamp : v.timestamp,
            budget: { ...snapshot, id: localBudgetId } as VersionedBudget['budget'],
            timestamp: new Date(v.timestamp),
            description: v.description,
          };
        }),
        currentVersionIndex: versions.length - 1,
      };
    });

    // Preserve existing local histories for budgets Convex doesn't know about
    // (local-only budgets). Histories of remotely deleted budgets were already
    // removed by the accept flow above, so anything left here is still live.
    const localState = getVersioningState();
    for (const [budgetIdStr, history] of Object.entries(localState.histories)) {
      const budgetId = Number(budgetIdStr);
      if (!(budgetId in histories)) {
        histories[budgetId] = history;
      }
    }

    setVersioningState({ histories }, true);
  }, [isAuthenticated, convexVersionHistories]);

  // On local write, push changed budget to Convex (delta sync)
  useEffect(() => {
    if (!isAuthenticated) return;

    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;

      // Skip events from Convex writes (source marker set by writeStorageData)
      if (detail?.source === 'convex') return;

      // Skip version navigation and undo/redo (local-only state, not data mutations)
      if (detail?.versionNavigation || detail?.undo || detail?.redo) return;

      const data = getStorageDataRaw();
      const idMap = getConvexIdMap();
      const changedBudgetId: number | undefined = detail?.changedBudgetId;
      const isDeleted = detail?.deleted === true;

      (async () => {
        try {
          if (isDeleted && changedBudgetId != null) {
            // Delta delete: remove only the deleted budget
            const convexId = idMap[changedBudgetId] as Id<"budgets"> | undefined;
            if (convexId) {
              try {
                await removeBudgetMutation({ id: convexId });
                delete pushedRevs.current[convexId];
                const newMap = { ...getConvexIdMap() };
                delete newMap[changedBudgetId];
                setConvexIdMap(newMap);
              } catch (err) {
                console.error('Failed to delete budget:', err);
                addToOfflineQueue({ type: 'deleteBudget', payload: { id: convexId } });
              }
            }
          } else if (changedBudgetId != null) {
            // Skip if a push is already in flight for this budget
            if (pushInFlight.current.has(changedBudgetId)) return;
            pushInFlight.current.add(changedBudgetId);
            try {
              // Delta update/create: sync only the changed budget
              const budget = data.budgets.find((b) => b.id === changedBudgetId);
              if (budget) {
                const convexId = idMap[changedBudgetId] as Id<"budgets"> | undefined;
                const doc = budgetToConvexDoc(budget);
                const payload = { ...doc, updatedAt: Date.now() };
                try {
                  if (convexId) {
                    const newRev = await updateBudgetMutation({ id: convexId, budget: payload });
                    // Record rev AFTER successful push so failures don't leave stale
                    // echoes. Old backends (deploy window) return the doc Id — treat
                    // that as rev 0 so echo detection stays consistent.
                    pushedRevs.current[convexId] = typeof newRev === 'number' ? newRev : 0;
                  } else {
                    const newId = await createBudgetMutation({ budget: payload });
                    pushedRevs.current[newId] = 1;
                    setConvexIdMap({ ...getConvexIdMap(), [changedBudgetId]: newId });
                  }
                } catch (err) {
                  console.error('Failed to sync budget:', err);
                  if (convexId) {
                    addToOfflineQueue({
                      type: 'updateBudget',
                      payload: { id: convexId, budget: payload },
                    });
                  } else if (!queuedCreates.current.has(changedBudgetId)) {
                    queuedCreates.current.add(changedBudgetId);
                    addToOfflineQueue({
                      type: 'createBudget',
                      payload: { budget: payload },
                      meta: { localBudgetId: changedBudgetId },
                    });
                  }
                }
              }
            } finally {
              pushInFlight.current.delete(changedBudgetId);
            }
          }

          // Always sync default budget preference
          const defaultLocal = data.defaultBudgetId;
          const currentIdMap = getConvexIdMap();
          const defaultConvex = defaultLocal != null ? (currentIdMap[defaultLocal] as Id<"budgets"> | undefined) ?? null : null;
          pushedDefaultConvexId.current = defaultConvex;
          try {
            await setDefaultBudgetIdMutation({ defaultBudgetId: defaultConvex });
          } catch (err) {
            console.error('Failed to set default budget:', err);
            addToOfflineQueue({
              type: 'setDefaultBudget',
              payload: { defaultBudgetId: defaultConvex },
            });
          }
          setLastSyncDate(new Date());
        } catch (err) {
          console.error('Convex push failed:', err);
        }
      })();
    };

    window.addEventListener(BUDGET_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(BUDGET_DATA_UPDATED_EVENT, handler);
  }, [
    isAuthenticated,
    updateBudgetMutation,
    createBudgetMutation,
    removeBudgetMutation,
    setDefaultBudgetIdMutation,
    setLastSyncDate,
  ]);

  // On version creation, push to Convex
  useEffect(() => {
    if (!isAuthenticated) return;

    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const { budgetId, version } = detail;

      const idMap = getConvexIdMap();
      const convexBudgetId = idMap[budgetId] as Id<"budgets"> | undefined;

      if (!convexBudgetId) {
        // Budget hasn't been pushed to Convex yet — skip version sync.
        // The version is already saved locally; it will be synced when
        // the budget is eventually pushed and the ID map is populated.
        return;
      }

      createVersionMutation({
        budgetId: convexBudgetId,
        snapshot: version.budget,
        description: version.description,
      }).catch((err) => {
        console.error('Failed to sync version to Convex:', err);
        addToOfflineQueue({
          type: 'createVersion',
          payload: {
            budgetId: convexBudgetId,
            snapshot: version.budget,
            description: version.description,
          },
        });
      });
    };

    window.addEventListener(VERSION_CREATED_EVENT, handler);
    return () => window.removeEventListener(VERSION_CREATED_EVENT, handler);
  }, [isAuthenticated, createVersionMutation]);

  // Process offline queue when authenticated + periodic retry
  useEffect(() => {
    if (!isAuthenticated || importInProgress.current) return;

    processOfflineQueue();

    const interval = setInterval(() => {
      processOfflineQueue();
    }, OFFLINE_QUEUE_RETRY_INTERVAL);

    return () => clearInterval(interval);
  }, [isAuthenticated, processOfflineQueue]);

  // Sync conflict resolution handlers
  const handlePushToCloud = async () => {
    if (!syncConflict) return;
    setSyncConflict(null);
    try {
      await performImport();
    } catch (err) {
      console.error('Convex import failed:', err);
    }
  };

  const handleClearLocal = () => {
    // Clear budgets from localStorage (skip event — no need to trigger sync push)
    writeStorageData({ budgets: [], defaultBudgetId: null }, true);
    setVersioningState({ histories: {} }, true);
    // Clear ALL sync state so Convex data can flow back cleanly
    setConvexIdMap({});
    clearConvexImportDone();
    // Reset in-memory tracking refs
    pushedRevs.current = {};
    hasAcceptedRemoteData.current = false;
    // NOT cancelled: with local now empty, the next pull pass resolves to
    // mark-done / accept-remote on its own, letting cloud data flow back
    cancelledThisSession.current = false;
    setSyncConflict(null);
  };

  const handleImportFromCloud = () => {
    // Accept the cloud data and discard local budgets: clear local state so
    // the pull flow (re-armed by setting import done) imports cloud cleanly.
    writeStorageData({ budgets: [], defaultBudgetId: null }, true);
    setVersioningState({ histories: {} }, true);
    setConvexIdMap({});
    pushedRevs.current = {};
    hasAcceptedRemoteData.current = false;
    setConvexImportDone();
    if (typeof currentUserId === 'string') setLastSyncedUserId(currentUserId);
    setSyncConflict(null);
  };

  const handleCancel = () => {
    setSyncConflict(null);
    cancelledThisSession.current = true;
  };

  return (
    <SyncConflictDialog
      isOpen={!!syncConflict}
      type={syncConflict?.type ?? 'local-has-data'}
      localBudgetCount={syncConflict?.localBudgetCount ?? 0}
      cloudBudgetCount={syncConflict?.cloudBudgetCount ?? 0}
      onPushToCloud={handlePushToCloud}
      onClearLocal={handleClearLocal}
      onImportFromCloud={handleImportFromCloud}
      onCancel={handleCancel}
    />
  );
}

export function ConvexSyncLayer() {
  const available = useConvexAvailable();
  if (!available) return null;
  return <ConvexSyncLayerInner />;
}
